/**
 * A NÉZŐ ADATA — a fájl bájtjai és a megnyitott lapforrás.
 *
 * A BÁJTOKAT EGYSZER kérjük le, és mindenki azokat használja: a rajzolás,
 * a letöltés és a nyomtatás is. Az aláírt cím öt percig él; ha a letöltés és
 * a nyomtatás a címet használná, egy hosszabb munka végén 401-be futna.
 *
 * A BIZONYLATVÁLTÁS megszakítja a félbemaradt letöltést, és a régi forrást
 * eldobja: sor-módban a könyvelő másodpercenként lép, és egy 25 MiB-os PDF
 * letöltése nem futhat tovább egy olyan papírért, amit már elhagyott.
 */

import { useEffect, useState } from 'react';
import { fetchFile } from '@/lib/api-client';
import { openSource, type PageSource, type SourceKind } from './source';

/**
 * MEDDIG VÁRUNK A FÁJLRA. A felső korlát 25 MiB; egy lassú, 2 Mbit/s-os
 * vonalon az is két perc alatt megjön. Korlát nélkül egy elakadt letöltés a
 * panelt örökre „töltődik"-ben hagyná.
 */
export const FILE_TIMEOUT_MS = 120_000;

export type ViewerSourceState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; source: PageSource; blob: Blob };

export function useViewerSource(url: string, kind: SourceKind): ViewerSourceState {
  /**
   * AZ ÁLLAPOT A CÍMHEZ KÖTVE. Bizonylatváltáskor az effekt csak a render
   * UTÁN fut le; addig a régi `ready` állna itt — egy olyan forrással, amit
   * a takarítás épp eldob. A címmel együtt tárolva az új bizonylat első
   * renderje is már „töltés", és a néző a régi papír állapotával (forgatás,
   * oldal, nagyítás) együtt leválik.
   */
  const [state, setState] = useState<{ url: string; value: ViewerSourceState }>({
    url,
    value: { status: 'loading' },
  });

  useEffect(() => {
    const abort = new AbortController();
    let opened: PageSource | null = null;
    let current = true;
    setState({ url, value: { status: 'loading' } });
    const timer = setTimeout(() => abort.abort(), FILE_TIMEOUT_MS);

    fetchFile(url, abort.signal)
      .then(async (blob) => {
        clearTimeout(timer);
        const source = await openSource(blob, kind);
        if (!current) {
          source.destroy();
          return;
        }
        opened = source;
        setState({ url, value: { status: 'ready', source, blob } });
      })
      .catch(() => {
        // A BUKÁS OKA itt sem a könyvelőé: sérült fájl, hálózat, lejárt cím.
        // Egyikkel sincs mit tennie, és a lefűzéshez a papír nem kell.
        if (current) {
          setState({ url, value: { status: 'failed' } });
        }
      });

    return () => {
      current = false;
      clearTimeout(timer);
      abort.abort();
      opened?.destroy();
    };
  }, [url, kind]);

  return state.url === url ? state.value : { status: 'loading' };
}
